'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b">
        <div className="container max-w-4xl mx-auto px-4 py-4">
          <Link href="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="container max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8 text-purple-600" />
          <h1 className="text-4xl font-bold">Privacy Policy</h1>
        </div>

        <p className="text-muted-foreground mb-8">
          Last updated: January 2025
        </p>

        <div className="prose dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">Overview</h2>
            <p className="text-muted-foreground">
              At Apprendo, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform for capturing and sharing knowledge from team conversations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Information We Collect</h2>
            <h3 className="text-xl font-semibold mb-2">Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Account information (name, email, company)</li>
              <li>Conversation recordings and transcripts</li>
              <li>User-generated content and moments</li>
              <li>Communication preferences</li>
            </ul>

            <h3 className="text-xl font-semibold mt-4 mb-2">Automatically Collected Information</h3>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Usage data and analytics</li>
              <li>Device and browser information</li>
              <li>IP addresses and location data</li>
              <li>Cookies and similar technologies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>To provide and improve our services</li>
              <li>To process and transcribe your conversations</li>
              <li>To identify and surface key moments using AI</li>
              <li>To communicate with you about our services</li>
              <li>To ensure platform security and prevent fraud</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Data Security</h2>
            <p className="text-muted-foreground">
              We implement enterprise-grade security measures including:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
              <li>End-to-end encryption for all conversations</li>
              <li>SOC 2 Type II certified infrastructure</li>
              <li>Regular security audits and penetration testing</li>
              <li>Strict access controls and audit logging</li>
              <li>Automatic PII detection and redaction</li>
            </ul>
          </section>

          <section className="border-2 border-blue-200 dark:border-blue-900/50 rounded-lg p-6 bg-blue-50/50 dark:bg-blue-950/20">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              Enterprise Private Cloud Options
            </h2>
            <p className="text-muted-foreground mb-4">
              For Enterprise customers requiring complete data sovereignty, we offer private cloud deployment options:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>Your Cloud, Your Control:</strong> Store all videos, transcripts, and moments in your own AWS S3 buckets, Azure Blob Storage, or Google Cloud Storage</li>
              <li><strong>Dedicated Infrastructure:</strong> Deploy Apprendo within your VPC or private cloud environment</li>
              <li><strong>Compliance Ready:</strong> Meet HIPAA, GDPR, and other regulatory requirements with your existing cloud compliance certifications</li>
              <li><strong>Zero Data Residency:</strong> Your content never touches our servers—processing happens entirely within your infrastructure</li>
              <li><strong>Hybrid Options:</strong> Keep sensitive content on-premise while using our cloud for non-sensitive collaboration</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-4 italic">
              Contact our Enterprise team to discuss private deployment options tailored to your security requirements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Data Sharing</h2>
            <p className="text-muted-foreground">
              We do not sell your personal information. We may share information only:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
              <li>With your explicit consent</li>
              <li>With service providers under strict confidentiality agreements</li>
              <li>To comply with legal requirements</li>
              <li>To protect rights and safety</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Your Rights</h2>
            <p className="text-muted-foreground">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Export your data in a portable format</li>
              <li>Opt-out of marketing communications</li>
              <li>Restrict processing of your data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Compliance</h2>
            <p className="text-muted-foreground">
              We comply with:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
              <li>GDPR (General Data Protection Regulation)</li>
              <li>CCPA (California Consumer Privacy Act)</li>
              <li>HIPAA (where applicable)</li>
              <li>Industry-specific regulations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Data Retention</h2>
            <p className="text-muted-foreground">
              We retain your information only as long as necessary to provide our services and comply with legal obligations. You can request deletion of your data at any time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
            <p className="text-muted-foreground">
              For privacy-related questions or to exercise your rights, contact us at:
            </p>
            <p className="text-muted-foreground mt-2">
              Email: privacy@apprendo.co<br />
              Address: Apprendo Inc., San Francisco, CA
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Updates to This Policy</h2>
            <p className="text-muted-foreground">
              We may update this Privacy Policy from time to time. We will notify you of any material changes via email or through our platform.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}